import { Eye, X } from "@tamagui/lucide-icons";
import {
  Customer,
  SensorAlert,
  SensorAlertSeverity,
  SensorAlertType,
  useAlertStatusCounts,
  useCustomers,
} from "common";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import {
  Button,
  Heading,
  Label,
  ScrollView,
  Separator,
  Spinner,
  Text,
  XStack,
  YStack,
  useMedia,
} from "tamagui";
import { GradientButton, OutlinedButton, Table } from "tamagui-config";

import { GradientCard } from "../../components/GradientCard";
import { Select } from "../../components/Select";
import { StatusPill } from "../../components/StatusPill";
import { Tabs } from "../../components/Tabs";
import { useStatusPillProps } from "../../hooks/useStatusPillProps";
import { tooltip } from "../../utils/a11y";
import { formatDate, formatTime } from "../../utils/formatDate";

enum Tab {
  Active = "active",
  Cleared = "cleared",
}

export const Alerts = () => {
  const { customers, loading: isCustomersLoading } = useCustomers() || {};
  // Deep-linkable: /alerts?tab=cleared&type=SENSOR_INACTIVE&severity=high&customer=<id>.
  // Read once on mount; the filters stay local state after that.
  const [searchParams] = useSearchParams();
  const fromUrl = (key: string, allowed: string[]) => {
    const value = searchParams.get(key) ?? "";
    return allowed.includes(value) ? value : "";
  };
  const [selectedTab, setSelectedTab] = useState<Tab>(
    (fromUrl("tab", Object.values(Tab)) as Tab) || Tab.Active,
  );
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onTabChange = (value: Tab) => {
    setSelectedTab(value);
  };

  const customerFilterOptions = useMemo(
    () => [
      { label: t("common.all"), value: "" },
      ...(customers || [])
        .filter((customer) => !!customer.name)
        .map((customer) => ({
          label: customer.name as string,
          value: customer.id,
        })),
    ],
    [customers],
  );
  const typeFilterOptions = [
    { label: t("common.all"), value: "" },
    ...Object.values(SensorAlertType)
      .filter((value, index, all) => all.indexOf(value) === index)
      .map((value) => ({ label: alertTypeLabel(t, value), value })),
  ];
  const severityFilterOptions = [
    { label: t("common.all"), value: "" },
    ...Object.entries(SensorAlertSeverity).map(([key, value]) => ({
      label: t([
        `alerts.severity.${key[0].toLowerCase()}${key.slice(1)}`,
        "alerts.severity.unknown",
      ]),
      value,
    })),
  ];


  // ?age=7d → only alerts raised more than 7 days ago. The API has no
  // timestamp filter, so this pulls one big page and filters client-side.
  // ponytail: fine while active alerts < 1000; add an `olderThan` resolver arg
  // if the fleet outgrows that.
  const ageDays = Number(/^(\d+)d$/.exec(searchParams.get("age") ?? "")?.[1]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(ageDays ? 1000 : 10);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [pageTokens, setPageTokens] = useState<(string | undefined)[]>([
    undefined,
  ]);
  const [hasMorePages, setHasMorePages] = useState(false);

  // Customer ids are validated against the list once it loads, below.
  const [customerFilter, setCustomerFilter] = useState(
    searchParams.get("customer") ?? "",
  );
  const [typeFilter, setTypeFilter] = useState(
    fromUrl("type", Object.values(SensorAlertType)),
  );
  const [severityFilter, setSeverityFilter] = useState(
    fromUrl("severity", Object.values(SensorAlertSeverity)),
  );

  const { counts: alertStatusCounts } = useAlertStatusCounts({
    customerId: customerFilter || undefined,
    alertType: typeFilter || undefined,
    severity: severityFilter || undefined,
  });

  const [displayedAlerts, setDisplayedAlerts] = useState<SensorAlert[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPagedAlerts = async (pageToken?: string) => {
    setIsLoading(true);
    try {
      const { API, graphql } = await import("common");

      const filters = {
        customerId: customerFilter || undefined,
        alertType: typeFilter || undefined,
        severity: severityFilter || undefined,
        isAcknowledged:
          selectedTab === Tab.Active
            ? false
            : selectedTab === Tab.Cleared
              ? true
              : undefined,
      };

      const response = await API.graphql({
        query: graphql.listSensorAlerts,
        variables: {
          ...filters,
          limit: pageSize,
          nextToken: pageToken,
        },
      });

      const responseData = (response as any).data?.listSensorAlerts;
      const ageCutoff = ageDays ? Date.now() / 1000 - ageDays * 86400 : null;
      const alerts = (responseData?.items || []).filter(
        (alert: SensorAlert) =>
          ageCutoff === null || (alert.timestamp ?? Infinity) < ageCutoff,
      );
      const newNextToken = responseData?.nextToken;

      const sortedAlerts = alerts.sort((a: SensorAlert, b: SensorAlert) => {
        const aTime = a.timestamp || 0;
        const bTime = b.timestamp || 0;
        return bTime - aTime;
      });

      setDisplayedAlerts(sortedAlerts);
      setNextToken(newNextToken);
      setHasMorePages(!!newNextToken);
    } catch (error) {
      console.error("Error fetching paged alerts:", error);
      setDisplayedAlerts([]);
      setHasMorePages(false);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setCustomerFilter(customerFilterOptions[0].value);
    setTypeFilter(typeFilterOptions[0].value);
    setSeverityFilter(severityFilterOptions[0].value);
  };

  const onViewAlertPress = (sensorAlertId: string) => {
    navigate(`/alerts/${sensorAlertId}`);
  };

  const goToNextPage = () => {
    if (hasMorePages && nextToken) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);

      const newPageTokens = [...pageTokens];
      if (newPageTokens.length === newPage) {
        newPageTokens.push(nextToken);
      }
      setPageTokens(newPageTokens);

      fetchPagedAlerts(nextToken);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);

      const previousToken = pageTokens[newPage - 1];
      fetchPagedAlerts(previousToken);
    }
  };

  const resetPagination = () => {
    setCurrentPage(1);
    setNextToken(undefined);
    setPageTokens([undefined]);
    setHasMorePages(false);
  };

  useEffect(() => {
    resetPagination();
    fetchPagedAlerts();
  }, [customerFilter, typeFilter, severityFilter, selectedTab]);

  if (isCustomersLoading || isLoading) {
    return (
      <YStack flex={1} gap="$2" justifyContent="center" alignItems="center">
        <Spinner size="large" />
        <Text>{t("common.loading")}</Text>
      </YStack>
    );
  }

  return (
    <YStack gap="$5">
      <Heading
        size="$14"
        fontSize="$14"
        $xs={{ fontSize: "$12", lineHeight: "$12" }}
      >
        {t("alerts.title")}
      </Heading>

      <Tabs value={selectedTab} onChange={onTabChange}>
        <Tabs.Button
          value={Tab.Active}
          count={alertStatusCounts?.activeCount || 0}
        >
          {t("alerts.tabs.active")}
        </Tabs.Button>
        <Tabs.Button
          value={Tab.Cleared}
          count={alertStatusCounts?.clearedCount || 0}
        >
          {t("alerts.tabs.cleared")}
        </Tabs.Button>
      </Tabs>

      <GradientCard backgroundColor="$transparentBorder" backgroundImage="none">
        <XStack
          gap="$3"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <XStack gap="$2.5" alignItems="center" flexWrap="wrap" flexShrink={1}>
            <Text fontWeight="bold" color="$white12">
              {t("alerts.filters.heading")}
            </Text>
            {ageDays ? (
              <Text fontSize="$3" color="$white10" flexShrink={1}>
                {t("alerts.filters.olderThan", { count: ageDays })}
              </Text>
            ) : null}
          </XStack>
          <Button
            variant="outlined"
            height={36}
            paddingVertical={0}
            paddingHorizontal="$3"
            onPress={clearFilters}
          >
            <X size={20} />
            {t("alerts.filters.clear")}
          </Button>
        </XStack>
        {/* Phone: customer on its own row, type and status share the next. */}
        <XStack gap="$3.5" flexWrap="wrap">
          <YStack
            gap="$2"
            flexGrow={1}
            flexBasis={0}
            $xs={{ flexBasis: "100%" }}
          >
            <Label
              htmlFor="customer-filter"
              fontSize="$3"
              lineHeight="$2"
              color="$white10"
            >
              {t("alerts.filters.customer")}
            </Label>
            <Select
              id="customer-filter"
              options={customerFilterOptions}
              value={customerFilter}
              onValueChange={setCustomerFilter}
            />
          </YStack>
          <YStack gap="$2" flexGrow={1} flexBasis={0}>
            <Label
              htmlFor="type-filter"
              fontSize="$3"
              lineHeight="$2"
              color="$white10"
            >
              {t("alerts.filters.type")}
            </Label>
            <Select
              id="type-filter"
              options={typeFilterOptions}
              value={typeFilter}
              onValueChange={setTypeFilter}
            />
          </YStack>
          <YStack gap="$2" flexGrow={1} flexBasis={0}>
            <Label
              htmlFor="severity-filter"
              fontSize="$3"
              lineHeight="$2"
              color="$white10"
            >
              {t("alerts.filters.severity")}
            </Label>
            <Select
              id="severity-filter"
              options={severityFilterOptions}
              value={severityFilter}
              onValueChange={setSeverityFilter}
            />
          </YStack>
        </XStack>
      </GradientCard>

      <Separator />

      <AlertsTable
        alerts={displayedAlerts}
        customers={customers}
        onViewAlertPress={onViewAlertPress}
      />
      <XStack
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap="$3"
        marginTop="$6"
        paddingHorizontal="$4"
        $xs={{ paddingHorizontal: 0 }}
      >
        <Text fontSize="$3" color="$gray10">
          Page {currentPage} {hasMorePages ? "(more available)" : "(last page)"}
        </Text>
        <XStack gap="$2">
          <OutlinedButton
            size="$3"
            onPress={goToPreviousPage}
            disabled={currentPage === 1}
          >
            Previous
          </OutlinedButton>
          <GradientButton
            size="$3"
            onPress={goToNextPage}
            disabled={!hasMorePages}
          >
            Next
          </GradientButton>
        </XStack>
      </XStack>
    </YStack>
  );
};

const AlertsTable = ({
  alerts,
  customers,
  onViewAlertPress,
}: {
  alerts: SensorAlert[];
  customers: Customer[];
  onViewAlertPress: (sensorAlertId: string) => void;
}) => {
  const { t } = useTranslation();
  const { sm, lg } = useMedia();
  // The API resolves hub/sensor/customer per alert; the customers list is
  // only a fallback for rows enriched by an older backend.
  const customerOf = (alert: SensorAlert) =>
    alert.customer ??
    customers.find((customer) => customer.id === alert.customerId);

  if (sm) {
    return (
      <YStack gap="$3">
        {alerts.map((alert) => (
          <AlertsTableRow
            key={alert.id}
            asCard
            alert={alert}
            customer={customerOf(alert)}
            onViewAlertPress={onViewAlertPress}
            isFirst={false}
            isLast={false}
          />
        ))}
      </YStack>
    );
  }

  const table = (
    <Table width="100%">
      <Table.Head>
        <Table.Row rowLocation="first">
          <Table.HeaderCell>{t("alerts.table.alert")}</Table.HeaderCell>
          <Table.HeaderCell>{t("alerts.table.sensor")}</Table.HeaderCell>
          <Table.HeaderCell>{t("alerts.table.hub")}</Table.HeaderCell>
          <Table.HeaderCell>{t("alerts.table.customer")}</Table.HeaderCell>
          <Table.HeaderCell>{t("alerts.table.date")}</Table.HeaderCell>
          <Table.HeaderCell>{t("alerts.table.details")}</Table.HeaderCell>
          <Table.HeaderCell>{t("alerts.table.severity")}</Table.HeaderCell>
          <Table.HeaderCell>{t("alerts.table.action")}</Table.HeaderCell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {alerts.map((alert, index) => (
          <AlertsTableRow
            key={index}
            alert={alert}
            customer={customerOf(alert)}
            onViewAlertPress={onViewAlertPress}
            isFirst={index === 0}
            isLast={index === alerts.length - 1}
          />
        ))}
      </Table.Body>
    </Table>
  );

  // Below full desktop the eight columns crush into per-letter wrapping, so
  // the table keeps its 1440px-window width and scrolls sideways instead.
  // Above it the table fits; a scroller there would clip the View buttons.
  return lg ? (
    <ScrollView
      horizontal
      contentContainerStyle={{ width: "100%", minWidth: 1100 }}
    >
      {table}
    </ScrollView>
  ) : (
    table
  );
};

const CardLine = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <XStack gap="$3" alignItems="baseline" justifyContent="space-between">
    <Text fontSize="$3" color="$gray11" flexShrink={0}>
      {label}
    </Text>
    <YStack flexShrink={1} alignItems="flex-end">
      {children}
    </YStack>
  </XStack>
);

const AlertsTableRow = ({
  alert,
  customer,
  onViewAlertPress,
  isFirst,
  isLast,
  asCard,
}: {
  alert: SensorAlert;
  customer?: Customer;
  onViewAlertPress: (sensorAlertId: string) => void;
  isFirst: boolean;
  isLast: boolean;
  asCard?: boolean;
}) => {
  const { generateAlertStatusPillProps } = useStatusPillProps();
  const statusPillProps = generateAlertStatusPillProps(alert);
  const { t } = useTranslation();

  const typeLabel = alertTypeLabel(t, alert.alertType);
  const date = new Date((alert.timestamp || 0) * 1000);
  const sensor = alert.sensor;
  // HUB_OFFLINE / HUB_INACTIVE / HUB_RADIO_FAULT rows carry the hub id as
  // sensorId — there is no sensor, the base station itself is the subject.
  const isHubAlert =
    alert.alertType.startsWith("HUB_") || alert.sensorId === alert.hubId;
  const sensorLabel = isHubAlert
    ? t("alerts.table.baseStation")
    : [sensor?.name, sensor?.location].filter(Boolean).join(" · ") ||
      t("alerts.table.unknownSensor");
  const hubLabel = alert.hub?.name || alert.hubId?.slice(0, 8) || "";

  // Format humidity alert messages with additional details
  const formatHumidityAlertMessage = (originalMessage: string): string => {
    if (alert.alertType === SensorAlertType.HighHumidity) {
      const humidity = alert.value || "N/A";
      const location = sensor?.location || "Unknown Location";

      const time = formatTime(date);
      const formattedDate = formatDate(date);

      // Map severity to descriptive text with humidity details
      switch (alert.severity) {
        case "medium":
          return `Slightly Humid (${humidity}%) in ${location} at ${time} on ${formattedDate}`;
        case "high":
          return `Humid (${humidity}%) in ${location} at ${time} on ${formattedDate}`;
        case "critical":
          return `Extremely Humid (${humidity}%) in ${location} at ${time} on ${formattedDate}`;
        default:
          return `Humid (${humidity}%) in ${location} at ${time} on ${formattedDate}`;
      }
    }
    return originalMessage || "";
  };

  const dateLabel = t("alerts.table.dateValue", { date });
  const details = formatHumidityAlertMessage(alert.message || "");
  const customerInfo = (textAlign?: "right") =>
    customer && (
      <>
        <Text textAlign={textAlign}>{customer.name}</Text>
        <Text fontSize="$3" color="$white10" textAlign={textAlign}>
          {t("alerts.table.customerAddress", {
            street: customer.address,
            city: customer.city,
            state: customer.state,
            zip: customer.zipCode,
          })}
        </Text>
      </>
    );

  if (asCard) {
    // Phone: what and how bad first, then when, then where it is.
    return (
      <YStack
        tag="article"
        gap="$2.5"
        padding="$3.5"
        borderRadius="$6"
        borderWidth={1}
        borderColor="$borderColor"
        backgroundColor="$gray2"
      >
        <XStack gap="$3" alignItems="flex-start" justifyContent="space-between">
          <Text fontSize="$5" fontWeight="bold" flexShrink={1}>
            {typeLabel}
          </Text>
          <StatusPill {...statusPillProps} />
        </XStack>
        <Text fontSize="$3" color="$white10">
          {dateLabel}
        </Text>
        {details ? <Text>{details}</Text> : null}
        <CardLine label={t("alerts.table.sensor")}>
          <Text textAlign="right">{sensorLabel}</Text>
        </CardLine>
        <CardLine label={t("alerts.table.hub")}>
          <Text textAlign="right">{hubLabel}</Text>
        </CardLine>
        {customer && (
          <CardLine label={t("alerts.table.customer")}>
            {customerInfo("right")}
          </CardLine>
        )}
        <OutlinedButton
          rounded
          icon={Eye}
          fontSize="$3"
          marginTop="$1.5"
          alignSelf="flex-end"
          onPress={() => onViewAlertPress(alert.id)}
        >
          {t("alerts.table.viewButton")}
        </OutlinedButton>
      </YStack>
    );
  }

  return (
    <Table.Row
      key={alert.id}
      rowLocation={isFirst && !isLast ? "first" : isLast ? "last" : "middle"}
    >
      <Table.Cell>
        <Text fontWeight="bold">{typeLabel}</Text>
      </Table.Cell>
      <Table.Cell maxWidth="$12">
        <Text
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          width="100%"
          {...tooltip(sensorLabel)}
        >
          {sensorLabel}
        </Text>
      </Table.Cell>
      <Table.Cell maxWidth="$12">
        <Text
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          width="100%"
          {...tooltip(hubLabel)}
        >
          {hubLabel}
        </Text>
      </Table.Cell>
      <Table.Cell gap="$1.5">{customerInfo()}</Table.Cell>
      <Table.Cell>{dateLabel}</Table.Cell>
      <Table.Cell>
        <Text className="break-anywhere">{details}</Text>
      </Table.Cell>
      <Table.Cell>
        <StatusPill {...statusPillProps} />
      </Table.Cell>
      <Table.Cell>
        <OutlinedButton
          rounded
          icon={Eye}
          fontSize="$3"
          onPress={() => onViewAlertPress(alert.id)}
        >
          {t("alerts.table.viewButton")}
        </OutlinedButton>
      </Table.Cell>
    </Table.Row>
  );
};

/**
 * Alert-type label via i18n. Enum keys become camelCase i18n keys
 * ("HighBCTCurrent" → "highBCTCurrent"); unknown types fall back cleanly.
 */
function alertTypeLabel(t: (keys: string[]) => string, alertType: string): string {
  const key = Object.entries(SensorAlertType).find(
    ([, value]) => value === alertType,
  )?.[0];
  if (!key) return t(["alerts.type.unknown"]);
  return t([`alerts.type.${key[0].toLowerCase()}${key.slice(1)}`, "alerts.type.unknown"]);
}

export default Alerts;
